import { documentTime } from './record-dates';
import type { NormalizedTable, RecordSummary } from './types';

function headerKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const PLACEHOLDER = /^(?:n\/?a|not available|none|-+)$/i;

/** Convert only understood tables; unrecognized source columns remain visible. */
export function permitHistoryTableKind(table: NormalizedTable) {
  const headers = table.headers.map(headerKey);
  if (
    headers.includes('stage') &&
    headers.some((header) => ['actiondate', 'date'].includes(header))
  )
    return 'stage';
  if (
    headers.includes('inspectiondescription') &&
    headers.includes('inspectionstatus') &&
    headers.some((header) => ['activitydate', 'inspectiondate', 'date'].includes(header))
  )
    return 'inspection';
  return null;
}

const PERMIT_KINDS = new Set(['building', 'electrical', 'plumbing']);

export type PermitHistoryEvent = {
  key: string;
  date: string;
  label: string;
  comment: string;
  status: string;
  inspector: string;
  extra: Array<{ label: string; column: number; value: string }>;
};

/** Stage and inspection-result tables on any DBI permit page (building, electrical, plumbing). */
export function buildingPermitHistory(record: RecordSummary) {
  const stages: PermitHistoryEvent[] = [];
  const inspections: PermitHistoryEvent[] = [];
  const consumedTables = new Set<NormalizedTable>();
  if (!PERMIT_KINDS.has(record.kind)) return { stages, inspections, consumedTables };

  for (const page of record.pages ?? []) {
    for (const table of page.tables) {
      const kind = permitHistoryTableKind(table);
      if (!kind || !table.rows.length) continue;
      // A wider row cannot be faithfully labeled. Retain its original table.
      if (table.rows.some((row) => row.length > table.headers.length)) continue;
      const headers = table.headers.map(headerKey);
      const index = (keys: string[]) => headers.findIndex((header) => keys.includes(header));
      const dateIndex = index(['actiondate', 'activitydate', 'inspectiondate', 'date']);
      const labelIndex = index(kind === 'stage' ? ['stage'] : ['inspectiondescription']);
      const commentIndex = index(['comments', 'comment', 'remarks']);
      const statusIndex = kind === 'inspection' ? index(['inspectionstatus']) : -1;
      const inspectorIndex = index(['inspector']);
      const known = new Set([dateIndex, labelIndex, commentIndex, statusIndex, inspectorIndex]);
      const events = table.rows
        .filter((row) => row.some((value) => value.trim()))
        .map((row, rowIndex) => ({
          key: `${consumedTables.size}-${rowIndex}`,
          date: row[dateIndex] ?? '',
          label: row[labelIndex] ?? '',
          comment: row[commentIndex] ?? '',
          status: row[statusIndex] ?? '',
          inspector: row[inspectorIndex] ?? '',
          extra: table.headers.flatMap((header, columnIndex) =>
            !known.has(columnIndex) && row[columnIndex]
              ? [
                  {
                    label: header,
                    column: columnIndex + 1,
                    value: row[columnIndex]!,
                  },
                ]
              : [],
          ),
        }));
      if (!events.length) continue;
      consumedTables.add(table);
      (kind === 'stage' ? stages : inspections).push(...events);
    }
  }
  // Preserve the source's same-day order (triage, filing, approval, issue).
  const chronological = (a: PermitHistoryEvent, b: PermitHistoryEvent) =>
    (documentTime(a.date) ?? 0) - (documentTime(b.date) ?? 0);
  return {
    stages: stages.sort(chronological),
    inspections: inspections.sort(chronological),
    consumedTables,
  };
}

/**
 * DBI appointment and activity tables on any permit page. Not consumed from
 * the record view; they feed the case timeline so scheduled inspections and
 * trade-permit activity are visible without opening the record.
 */
export function permitScheduleEvents(record: RecordSummary) {
  const appointments: PermitHistoryEvent[] = [];
  const activity: PermitHistoryEvent[] = [];
  if (!PERMIT_KINDS.has(record.kind)) return { appointments, activity };
  (record.pages ?? []).forEach((page, pageIndex) => {
    page.tables.forEach((table, tableIndex) => {
      const headers = table.headers.map(headerKey);
      const index = (keys: string[]) => headers.findIndex((header) => keys.includes(header));
      const containing = (part: string) => headers.findIndex((header) => header.includes(part));
      const appointmentDate = index(['appointmentdate']);
      const activityDate = index(['activitydate']);
      // DBI lists the numeric code before its description; the code alone
      // ("128") tells the owner nothing.
      const activityLabel =
        [index(['activitycodedescr']), index(['activitycode'])].find((column) => column >= 0) ?? -1;
      const isAppointment = appointmentDate >= 0;
      const isActivity = record.kind !== 'building' && activityDate >= 0 && activityLabel >= 0;
      if (!isAppointment && !isActivity) return;
      const dateIndex = isAppointment ? appointmentDate : activityDate;
      const labelIndex = isAppointment
        ? Math.max(containing('description'), index(['appointmenttype']))
        : activityLabel;
      const typeIndex = isAppointment ? index(['appointmenttype']) : -1;
      const commentIndex = isAppointment ? -1 : index(['description']);
      const inspectorIndex = index(['inspector']);
      const comment = (row: string[]) => {
        const value = row[commentIndex] ?? '';
        return value === row[labelIndex] || PLACEHOLDER.test(value) ? '' : value;
      };
      table.rows
        .filter((row) => row.length <= table.headers.length && row[dateIndex])
        .forEach((row, rowIndex) =>
          (isAppointment ? appointments : activity).push({
            key: `${isAppointment ? 'appointment' : 'activity'}-${pageIndex}-${tableIndex}-${rowIndex}`,
            date: row[dateIndex] ?? '',
            label: isAppointment
              ? `Inspection appointment: ${row[labelIndex] ?? ''}`.trim()
              : (row[labelIndex] ?? ''),
            comment: isAppointment
              ? [row[typeIndex], row[index(['appointmentampm'])]].filter(Boolean).join(' · ')
              : comment(row),
            status: isAppointment ? 'Scheduled' : '',
            inspector: row[inspectorIndex] ?? '',
            extra: [],
          }),
        );
    });
  });
  return { appointments, activity };
}

/**
 * The dated history of a permit as one list: stages, inspection results,
 * plan-review holds, unresulted appointments and trade-permit activity.
 */
export function permitThread(record: RecordSummary) {
  const entries: Array<{
    key: string;
    date: string;
    label: string;
    status: string;
    comment: string;
    actor: string;
  }> = [];
  if (!PERMIT_KINDS.has(record.kind)) return entries;
  const history = buildingPermitHistory(record);
  history.stages.forEach((event) =>
    entries.push({
      key: `stage-${event.key}`,
      date: event.date,
      label: event.label,
      status: '',
      comment: event.comment,
      actor: '',
    }),
  );
  history.inspections.forEach((event) =>
    entries.push({
      key: `inspection-${event.key}`,
      date: event.date,
      label: event.label,
      status: event.status,
      comment: event.comment,
      actor: event.inspector,
    }),
  );
  record.addenda?.forEach((item, index) =>
    entries.push({
      key: `addenda-${index}`,
      date: item.date,
      label: item.station ? `${item.station} review` : 'Plan review',
      status: item.reviewResult,
      comment: item.holdDescription,
      actor: item.checkedBy,
    }),
  );
  const schedule = permitScheduleEvents(record);
  // Once the inspector has recorded a result, the appointment that booked
  // the visit is history: the result is the event.
  const resulted = (appointment: PermitHistoryEvent) =>
    entries.some(
      (entry) =>
        entry.key.startsWith('inspection-') &&
        entry.date === appointment.date &&
        entry.label &&
        appointment.label.toUpperCase().includes(entry.label.toUpperCase()),
    );
  [...schedule.appointments.filter((event) => !resulted(event)), ...schedule.activity].forEach(
    (event) =>
      entries.push({
        key: event.key,
        date: event.date,
        label: event.label,
        status: event.status,
        comment: event.comment,
        actor: event.inspector,
      }),
  );
  return entries;
}
